import { useMemo, useRef, useState } from "react";
import { useGetSet, useMount, useUnmount } from "react-use";
import {
  InputMuteUpdate,
  LiveChatJoinResponse,
  LivechatSubjectType,
  LiveChatUser,
  LiveChatUserUpdateAction,
  LiveChatUserUpdateEvent,
} from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import { debounce } from "lodash-es";
import useMounted from "hooks/useMounted";
import useOnAuthorised from "hooks/useOnAuthorised";
import providers from "lib/providers";

interface State {
  joinData: LiveChatJoinResponse | null;
  loading: boolean;
}

export type UserListUpdatedCB = (data: LiveChatUserUpdateEvent) => void;
// eslint-disable-next-line @typescript-eslint/no-empty-function
const emptyFunc = () => {};

export default function useLiveChat({
  livechatSubject,
  connectionDelay = 0,
  liveChatName,
}: {
  livechatSubject: Subject<LivechatSubjectType>;
  connectionDelay?: number;
  liveChatName?: string;
}) {
  const keepAliveStart = useRef<boolean>(true);
  const mounted = useMounted();
  const [getState, setState] = useGetSet<State>({
    joinData: null,
    loading: false,
  });

  const temp = getState();
  const userList = useMemo(() => (temp.joinData?.users ?? []).filter((i) => i.id !== temp.joinData?.me.id), [temp]);

  const [clientServerTimeDiff, setClientServerTimeDiffState] = useState(0);
  const setClientServerTimeDiff = (serverTime: Date) => {
    setClientServerTimeDiffState(new Date().getTime() - serverTime.getTime());
  };

  const updateUserList = (data: LiveChatUserUpdateEvent) => {
    setState((state) => {
      if (!state.joinData) {
        return state;
      }
      let users = state.joinData.users;
      if (data.action === LiveChatUserUpdateAction.DELETE) {
        users = state.joinData.users.filter((i) => i.id !== data.user.id);
      }
      if (data.action === LiveChatUserUpdateAction.UPDATE) {
        if (state.joinData.me.id === data.user.id) {
          state.joinData.me = data.user;
        }
        if (state.joinData.users.find((i) => data.user.id === i.id)) {
          users = state.joinData.users.map((i) => (i.id === data.user.id ? data.user : i));
        } else users = state.joinData.users.concat([data.user]);
      }
      return {
        ...state,
        joinData: {
          ...state.joinData,
          users,
        },
      };
    });
  };

  const endChat = async () => {
    const state = getState();
    if (!state.joinData) return;
    setState((prevState) => ({
      ...prevState,
      joinData: null,
    }));
    return providers.LivechatsProvider.end({ token: state.joinData.token });
  };

  const onUserListUpdatedCB = useRef<UserListUpdatedCB>(emptyFunc);
  const setOnUserListUpdatedCB = (cb: UserListUpdatedCB) => (onUserListUpdatedCB.current = cb);
  const userListUpdateUnsubscribe = useRef(emptyFunc);
  const subscribeUserListUpdated = async (joinData: LiveChatJoinResponse) => {
    const token = joinData.token ?? "";
    const subscription = await providers.LivechatsProvider.subscribeUserUpdate(
      token,
      (data: LiveChatUserUpdateEvent) => {
        if (!mounted.current) return;
        onUserListUpdatedCB.current(data);
        updateUserList(data);
      }
    );
    if (!mounted.current) {
      return;
    }
    userListUpdateUnsubscribe.current = () => {
      subscription.unsubscribe();
    };
  };

  // keepAlive was originally a setTimeout-based async recursion. That
  // shape has two bugs:
  //   1. JavaScript has no tail-call optimisation, so on a long call with
  //      a server that's intermittently throwing 5xx the V8 stack grows
  //      until the tab freezes — exactly the "after 2-4 minutes the chat
  //      just dies and reloads don't help" symptom users reported.
  //   2. There is no bail-out: keepAlive keeps retrying every second
  //      forever, even when the livechat session is actually dead on
  //      the server (e.g. token revoked), so the UI hangs in "joined"
  //      while no real signalling happens.
  //
  // Rewrite as a setInterval + bounded consecutive-failure counter +
  // explicit 401 handling. Cleanup unconditionally clears the interval
  // on unmount.
  const keepAliveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const keepAliveFailureCountRef = useRef(0);
  const KEEPALIVE_MAX_FAILURES = 5;
  const KEEPALIVE_INTERVAL_MS = 1000;
  const startKeepAlive = (token: string): void => {
    if (keepAliveIntervalRef.current) clearInterval(keepAliveIntervalRef.current);
    keepAliveFailureCountRef.current = 0;
    let inFlight = false;
    keepAliveIntervalRef.current = setInterval(async () => {
      if (!keepAliveStart.current || !mounted.current) return;
      if (inFlight) return; // backpressure: don't pile up if server is slow
      inFlight = true;
      try {
        await providers.LivechatsProvider.keepalive({ token });
        if (keepAliveFailureCountRef.current > 0) {
          // eslint-disable-next-line no-console
          console.info(`[livechat] keepAlive recovered after ${keepAliveFailureCountRef.current} failures`);
          keepAliveFailureCountRef.current = 0;
        }
      } catch (e) {
        keepAliveFailureCountRef.current += 1;
        const status = (e as { response?: { status?: number } })?.response?.status;
        // eslint-disable-next-line no-console
        console.warn(`[livechat] keepAlive failed (consecutive=${keepAliveFailureCountRef.current}, status=${status ?? "?"}):`, e);
        if (status === 401 || keepAliveFailureCountRef.current >= KEEPALIVE_MAX_FAILURES) {
          // eslint-disable-next-line no-console
          console.error(`[livechat] keepAlive bailing out (${status === 401 ? "auth lost" : "max failures"}) — ending livechat session`);
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
          keepAliveStart.current = false;
          // Surface to the conference UI so it can switch to "failed" + auto-rejoin path.
          try { window.dispatchEvent(new CustomEvent("livechat:keepalive-failed")); } catch { /* ignore */ }
        }
      } finally {
        inFlight = false;
      }
    }, KEEPALIVE_INTERVAL_MS);
  };

  const toggleMuteUser = debounce(async (user: LiveChatUser) => {
    const data: InputMuteUpdate = {
      chatUserId: user.id,
      muted: !user.muted,
    };
    await providers.LivechatsProvider.muteUpdate({ data });
  }, 500);

  const onCreated = async () => {
    setState((s) => ({ ...s, loading: true }));
    await new Promise((r) => setTimeout(r, connectionDelay));
    const joinData = await providers.LivechatsProvider.join({
      data: {
        subject: livechatSubject,
        ...(liveChatName ? { screenName: liveChatName } : {}),
      },
    });
    try {
      mounted.current && setState((s) => ({ ...s, joinData }));
      setClientServerTimeDiff(parseISO(joinData.serverTime));
      // todo fix lost connected users between join & subscribing end
      await subscribeUserListUpdated(joinData);
      startKeepAlive(joinData?.token ?? "");
    } finally {
      mounted.current && setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  useMount(() => {
    onCreated().catch((e) => {
      // eslint-disable-next-line no-console
      console.error("[livechat] Failed to join:", e);
      mounted.current && setState((s) => ({ ...s, loading: false }));
    });
  });
  useUnmount(() => {
    endChat();
    keepAliveStart.current = false;
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    userListUpdateUnsubscribe.current();
  });

  useOnAuthorised(async () => {
    try {
      await endChat();
      await onCreated();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[livechat] Re-join after auth failed:", e);
    }
  });

  return {
    loading: getState().loading,
    joinData: getState().joinData,
    token: getState().joinData?.token ?? "",
    userList,
    setOnUserListUpdatedCB,
    clientServerTimeDiff,
    endChat,
    toggleMuteUser,
  };
}
